import { useCallback, useContext } from 'react';

import { IconButton } from '@atlaskit/button/new';
import DropdownMenu, {
  DropdownItem,
  DropdownItemGroup,
} from '@atlaskit/dropdown-menu';
import SettingsIcon from '@atlaskit/icon/core/settings';
import ShowMoreHorizontalIcon from '@atlaskit/icon/core/show-more-horizontal';
import StarStarredIcon from '@atlaskit/icon/core/star-starred';
import StarUnstarredIcon from '@atlaskit/icon/core/star-unstarred';
import {
  SpotlightManager,
  SpotlightTarget,
  SpotlightTransition,
} from '@atlaskit/onboarding';
import Skeleton from '@atlaskit/skeleton';
import { token } from '@atlaskit/tokens';

import { CurrentDataContext } from 'src/app/current-data';
import { ChoreographedSpotlight } from 'src/components/ipm-choreographer/bitbucket-choreographer';
import { MenuItem } from 'src/components/navigation/src/types';
import { Project, Repository } from 'src/components/types';
import { publishUiEvent } from 'src/utils/analytics/publish';

import { REPO_SETTINGS_ID, PROJECT_SETTINGS_ID } from './constants';
import { useStarredObjects, type StarredObjectType } from './hooks';
import { StarredItemsContext } from './starred-items-context';

// spotlight is only used on the repo level
const SPOTLIGHT_TARGET_NAME = 'meatball-menu-starred-onboarding';

// Skeleton used inside the DropdownMenu component with icon placeholder
function DropdownItemSkeleton() {
  return (
    <DropdownItem
      isDisabled
      elemBefore={<Skeleton width="16px" height="16px" isShimmering />}
    >
      <Skeleton width="100%" height="16px" isShimmering />
    </DropdownItem>
  );
}

interface MenuItemDropdownMenuProps {
  /**
   * The type of object: 'repositories' or 'projects'
   */
  objectType: StarredObjectType;
  /**
   * The repository or project instance
   */
  resource?: Repository | Project;
  /**
   * Whether the object is currently starred (fallback when outside StarredItemsProvider)
   */
  isStarred?: boolean;
  /**
   * Callback when the star status changes
   */
  onStarredChange?: (isStarred: boolean) => void;
  /**
   * Menu items for the resource (used to find settings link)
   */
  menuItems?: MenuItem[];
  /**
   * Whether menu items are currently loading
   */
  isMenuItemsLoading?: boolean;
  /**
   * Function to trigger loading menu items (needed for the settings item to be rendered in dropdown)
   */
  loadMenuItems?: () => void;
  /**
   * Whether to show the starred onboarding spotlight on this menu's trigger button
   */
  showOnboarding?: boolean;
  /**
   * Callback when the user dismisses the onboarding spotlight
   */
  onOnboardingDismiss?: () => void;
}

export const MenuItemDropdownMenu = ({
  objectType,
  resource,
  isStarred: defaultIsStarred = false,
  onStarredChange: onStarredChangeCallback,
  menuItems,
  isMenuItemsLoading = false,
  loadMenuItems,
  showOnboarding = false,
  onOnboardingDismiss,
}: MenuItemDropdownMenuProps) => {
  const { workspace } = useContext(CurrentDataContext);
  const workspaceSlug = workspace?.slug;
  const starredContext = useContext(StarredItemsContext);

  const starredState =
    !starredContext || !objectType || !resource
      ? defaultIsStarred
      : starredContext.isStarred(objectType, resource);

  const onStarredChange = useCallback(
    (isStarred: boolean) => {
      if (starredContext?.setStarred && objectType && resource) {
        starredContext.setStarred(objectType, resource, isStarred);
      }
      onStarredChangeCallback?.(isStarred);
    },
    [starredContext, objectType, resource, onStarredChangeCallback]
  );

  const {
    isStarred,
    isLoading: isLoadingStarred,
    toggleStar,
  } = useStarredObjects({
    workspaceSlug,
    objectType,
    resource,
    starredState,
    onStarredChange,
  });

  const settingsId =
    objectType === 'repositories' ? REPO_SETTINGS_ID : PROJECT_SETTINGS_ID;
  const settingsMenuItem = (menuItems ?? []).find(
    item => item.id === settingsId
  );
  const hasLoadedMenuItems = menuItems !== undefined;
  const showLoadingSkeleton = isMenuItemsLoading && !hasLoadedMenuItems;

  const handleStarClick = useCallback(() => {
    publishUiEvent({
      action: 'clicked',
      actionSubject: 'button',
      actionSubjectId: 'starButton',
      source: 'sideNav',
      attributes: {
        objectType: objectType === 'repositories' ? 'repository' : 'project',
        starAction: isStarred ? 'unstar' : 'star',
        location: 'navItemMeatballMenu',
      },
    });
    toggleStar();
  }, [objectType, isStarred, toggleStar]);

  const handleOpenChange = useCallback(
    (attrs: { isOpen: boolean }) => {
      if (
        attrs.isOpen &&
        !hasLoadedMenuItems &&
        !isMenuItemsLoading &&
        loadMenuItems
      ) {
        loadMenuItems();
      }
    },
    [hasLoadedMenuItems, loadMenuItems, isMenuItemsLoading]
  );

  const dropdownMenu = (
    <DropdownMenu
      shouldRenderToParent
      placement="bottom-start"
      onOpenChange={handleOpenChange}
      trigger={({ triggerRef, ...props }) => (
        <IconButton
          {...props}
          icon={() => <ShowMoreHorizontalIcon size="small" label="" />}
          label="Actions"
          appearance="subtle"
          spacing="compact"
          ref={triggerRef}
          isLoading={isLoadingStarred}
        />
      )}
    >
      <DropdownItemGroup>
        <DropdownItem
          elemBefore={
            isStarred ? (
              <StarStarredIcon
                label=""
                color={token('color.icon.accent.yellow')}
              />
            ) : (
              <StarUnstarredIcon label="" />
            )
          }
          onClick={handleStarClick}
        >
          {isStarred ? 'Remove from starred' : 'Add to starred'}
        </DropdownItem>
        {showLoadingSkeleton && <DropdownItemSkeleton />}
        {/* settings menu item will only be included from API if user has correct perms */}
        {settingsMenuItem && (
          <DropdownItem
            elemBefore={<SettingsIcon label="" />}
            href={settingsMenuItem.url}
          >
            Settings
          </DropdownItem>
        )}
      </DropdownItemGroup>
    </DropdownMenu>
  );

  if (!showOnboarding) {
    return dropdownMenu;
  }

  return (
    <SpotlightManager>
      <SpotlightTarget name={SPOTLIGHT_TARGET_NAME}>
        {dropdownMenu}
      </SpotlightTarget>
      <SpotlightTransition>
        <ChoreographedSpotlight
          target={SPOTLIGHT_TARGET_NAME}
          heading="New sidebar context menu"
          messageId="starred-objects-onboarding"
          targetBgColor={token('elevation.surface')}
          actions={[
            {
              text: 'Got it',
              onClick: onOnboardingDismiss,
            },
          ]}
        >
          Star your favorite repositories and projects for quick access. If
          you&apos;re an admin, you can also manage repository settings directly
          from this menu.
        </ChoreographedSpotlight>
      </SpotlightTransition>
    </SpotlightManager>
  );
};
